import React from 'react';

import {FormControl} from '@mui/material';
import {useForm} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import API, {Types, NoFlyZoneUpdateRequest, useCsrfToken} from '@wheeebo/polaris-api';
import {AreaTypeNoFlyZones, CircleLoader, LoadError, ValidatableField, useErrorHandler} from '@wheeebo/polaris-ui';

import AreaEditForm from './AreaEditForm';

const msgs = defineMessages({
  inputNameLabel: {
    id: 'form.NoFlyZoneEditForm.input.nameLabel',
    defaultMessage: 'Name',
  },
  inputNamePlaceholder: {
    id: 'form.NoFlyZoneEditForm.input.namePlaceholder',
    defaultMessage: 'Geo fence name',
  },
  textTitle: {
    id: 'form.NoFlyZoneEditForm..text.title',
    defaultMessage: 'No-fly zone',
  },
  textContent: {
    id: 'form.NoFlyZoneEditForm..text.content',
    defaultMessage: 'A no-fly zone is an area where any aircraft is not allowed to enter.',
  },
});

interface Props {
  noFlyZoneId: Types.UUID;
  locked?: boolean;
}

function NoFlyZoneEditForm({noFlyZoneId, locked = false}: Props): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const {register, formState} = useForm({mode: 'onChange'});
  const {errors} = formState;
  const queryClient = useQueryClient();
  const [name, setName] = React.useState<string>();
  const {data, isError, isLoading} = useQuery(API.NoFlyZones.getKey(noFlyZoneId), () =>
    API.NoFlyZones.get(noFlyZoneId),
  );
  const {mutate: updateNoFlyZone} = useMutation<unknown, unknown, NoFlyZoneUpdateRequest>(
    (params: NoFlyZoneUpdateRequest) => API.NoFlyZones.update(csrfToken, noFlyZoneId, params),
    {
      onSuccess: () => queryClient.invalidateQueries(API.NoFlyZones.CacheKey),
      onError: showAndEmitJsErrorEvent,
    },
  );
  const handleSaveName = (name: string) => {
    if (name) {
      updateNoFlyZone({name});
      return true;
    }
    return false;
  };
  return (
    <>
      {isLoading ? <CircleLoader /> : null}
      {isError ? <LoadError /> : null}
      {data ? (
        <AreaEditForm
          title={intl.formatMessage(msgs.textTitle)}
          subHeader={intl.formatMessage(msgs.textContent)}
          areaContainer={data}
          onUpdate={(area: Types.Area) => updateNoFlyZone({area})}
          areaType={AreaTypeNoFlyZones}
          editable>
          <FormControl component="fieldset" fullWidth>
            <ValidatableField
              id="name"
              label={intl.formatMessage(msgs.inputNameLabel)}
              placeholder={intl.formatMessage(msgs.inputNamePlaceholder)}
              value={name}
              registerOptions={{
                value: data?.name,
                required: true,
                maxLength: 255,
                onChange: (e) => setName(e.target.value),
              }}
              onSave={handleSaveName}
              editable
              {...{register, errors, locked}}
            />
          </FormControl>
        </AreaEditForm>
      ) : null}
    </>
  );
}

export default React.memo(NoFlyZoneEditForm);
